import { useState } from "react";
import { useCompany, Company, CompanyMember } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Building2, Edit, Trash2, Users, Eye, ShieldCheck, UserPlus, UserMinus,
  MapPin, Phone, Mail, Globe, CheckCircle2
} from "lucide-react";
import { GSTINVerificationInput } from "@/components/common/GSTINVerificationInput";
import { GSTVerificationResult } from "@/types/gst";
import { extractFromGSTIN } from "@/lib/onboarding/gstUtils";

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh",
  "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

export default function CompaniesPage() {
  const { companies, activeCompany, switchCompany, addCompany, updateCompany, deleteCompany, addMember, removeMember, getUserCompanies } = useCompany();
  const { role } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState("overview");
  const [showForm, setShowForm] = useState(false);
  const [viewCompany, setViewCompany] = useState<Company | null>(null);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [showMemberForm, setShowMemberForm] = useState<string | null>(null);

  // New company form
  const [f, setF] = useState({
    name: "", legalName: "", gstin: "", pan: "", cin: "",
    address: "", city: "", state: "Maharashtra", pincode: "",
    phone: "", email: "", website: "",
    gstRegistered: true, compositionScheme: false, plan: "Starter",
  });

  // New member form
  const [mf, setMF] = useState({ userId: "", userName: "", email: "", role: "accountant" as CompanyMember["role"] });

  const displayCompanies = role === "super_admin" ? companies : getUserCompanies();

  const handleApplyGstVerificationForNew = (data: GSTVerificationResult) => {
    setF((prev) => ({
      ...prev,
      gstin: data.gstin,
      name: data.tradeName || data.legalName || prev.name,
      legalName: data.legalName || prev.legalName,
      pan: data.pan || prev.pan,
      address: data.address || prev.address,
      city: data.city || prev.city,
      state: data.state || prev.state,
      pincode: data.pincode || prev.pincode,
      gstRegistered: data.status === "Active",
      compositionScheme: data.compositionScheme !== undefined ? data.compositionScheme : prev.compositionScheme,
    }));
  };

  const handleApplyGstVerificationForEdit = (data: GSTVerificationResult) => {
    if (!editCompany) return;
    setEditCompany({
      ...editCompany,
      gstin: data.gstin,
      name: data.tradeName || data.legalName || editCompany.name,
      legalName: data.legalName || editCompany.legalName,
      pan: data.pan || editCompany.pan,
      address: data.address || editCompany.address,
      city: data.city || editCompany.city,
      state: data.state || editCompany.state,
      pincode: data.pincode || editCompany.pincode,
      gstRegistered: data.status === "Active",
      compositionScheme: data.compositionScheme !== undefined ? data.compositionScheme : editCompany.compositionScheme,
    });
  };

  const handleAdd = () => {
    if (!f.name || !f.gstin) return;
    addCompany({
      name: f.name, legalName: f.legalName || f.name, gstin: f.gstin, pan: f.pan,
      cin: f.cin || undefined, address: f.address, city: f.city, state: f.state,
      pincode: f.pincode, phone: f.phone, email: f.email,
      website: f.website || undefined, financialYearStart: "04", baseCurrency: "INR",
      gstRegistered: f.gstRegistered, compositionScheme: f.compositionScheme,
      status: "Active", plan: f.plan, onboardingCompleted: true,
    });
    toast({ title: "Company Created", description: `${f.name} is now active` });
    setShowForm(false);
    setF({ name: "", legalName: "", gstin: "", pan: "", cin: "", address: "", city: "", state: "Maharashtra", pincode: "", phone: "", email: "", website: "", gstRegistered: true, compositionScheme: false, plan: "Starter" });
  };

  const handleEdit = () => {
    if (!editCompany) return;
    updateCompany(editCompany.id, editCompany);
    toast({ title: "Updated", description: `${editCompany.name} updated` });
    setEditCompany(null);
  };

  const handleAddMember = () => {
    if (!showMemberForm || !mf.userName || !mf.email) return;
    addMember(showMemberForm, { userId: `u-${crypto.randomUUID().slice(0, 6)}`, userName: mf.userName, email: mf.email, role: mf.role });
    toast({ title: "Member Added", description: `${mf.userName} added` });
    setShowMemberForm(null);
    setMF({ userId: "", userName: "", email: "", role: "accountant" });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Companies</h1>
          <p className="text-sm text-muted-foreground">
            Manage companies — each with separate data, GST, inventory & reports
            {activeCompany && <span className="ml-2">| Active: <strong>{activeCompany.name}</strong></span>}
          </p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button className="gap-2"><Plus className="h-4 w-4" /> New Company</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create New Company</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-2">
              <div className="p-3 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/70 dark:border-blue-900/40 rounded-lg">
                <GSTINVerificationInput
                  value={f.gstin}
                  onChange={(val) => {
                    const extracted = extractFromGSTIN(val);
                    setF((prev) => ({
                      ...prev,
                      gstin: val,
                      pan: extracted.pan || prev.pan,
                      state: extracted.state?.name || prev.state,
                    }));
                  }}
                  onApplyVerification={handleApplyGstVerificationForNew}
                  label="GSTIN Auto-Fill & Verification"
                  required
                />
              </div>
              <div><Label>Company Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="ABC Trading Co." /></div>
              <div><Label>Legal Name</Label><Input value={f.legalName} onChange={(e) => setF({ ...f, legalName: e.target.value })} placeholder="Full registered name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PAN</Label><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value })} placeholder="AAAAA0000A" maxLength={10} /></div>
                <div><Label>CIN (optional)</Label><Input value={f.cin} onChange={(e) => setF({ ...f, cin: e.target.value })} placeholder="Company Identification Number" /></div>
              </div>
              <div><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Street address" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
                <div>
                  <Label>State</Label>
                  <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Pincode</Label><Input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} maxLength={6} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              </div>
              <div><Label>Website</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="www.company.com" /></div>
              <div className="flex items-center justify-between py-1">
                <Label>GST Registered</Label>
                <Switch checked={f.gstRegistered} onCheckedChange={(v) => setF({ ...f, gstRegistered: v })} />
              </div>
              <div className="flex items-center justify-between py-1">
                <Label>Composition Scheme</Label>
                <Switch checked={f.compositionScheme} onCheckedChange={(v) => setF({ ...f, compositionScheme: v })} />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Starter">Starter</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAdd} className="w-full gap-2"><Building2 className="h-4 w-4" /> Create Company</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Total Companies</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{displayCompanies.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Active</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{displayCompanies.filter((c) => c.status === "Active").length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Total Members</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{displayCompanies.reduce((s, c) => s + (c.members?.length || 0), 0)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Current Company</CardDescription></CardHeader>
          <CardContent>
            <div className="text-sm font-bold text-foreground">{activeCompany?.name || "None"}</div>
            {activeCompany && <p className="text-xs text-muted-foreground">{activeCompany.gstin}</p>}
          </CardContent>
        </Card>
      </div>

      {/* Company Cards */}
      {displayCompanies.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
            <Building2 className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold text-foreground">No Companies Registered</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-sm mx-auto">
            There are currently no companies registered in the platform. Click below to add the first company.
          </p>
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Create Company
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayCompanies.map((c) => (
          <Card key={c.id} className={`relative transition-all ${c.id === activeCompany?.id ? "ring-2 ring-primary" : ""}`}>
            {c.id === activeCompany?.id && (
              <div className="absolute top-3 right-3">
                <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" /> Active</Badge>
              </div>
            )}
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-base truncate">{c.name}</CardTitle>
                  <CardDescription className="truncate">{c.legalName}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ShieldCheck className="h-3 w-3" />
                  <code className="bg-muted px-1 rounded">{c.gstin}</code>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-3 w-3" /> {c.city}, {c.state}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Users className="h-3 w-3" /> {c.members?.length || 0} members
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Badge variant={c.status === "Active" ? "default" : c.status === "Trial" ? "secondary" : "outline"} className="text-[9px] h-4">{c.status}</Badge>
                  <span>{c.plan}</span>
                </div>
              </div>

              <div className="flex gap-1.5 pt-2 border-t border-border">
                {c.id !== activeCompany?.id && (
                  <Button size="sm" variant="default" className="flex-1 gap-1" onClick={() => { switchCompany(c.id); toast({ title: "Switched", description: `Now working on ${c.name}` }); }}>
                    Switch
                  </Button>
                )}
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setViewCompany(c)}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditCompany({ ...c })}>
                  <Edit className="h-3.5 w-3.5" />
                </Button>
                {(role === "super_admin" || role === "company_admin") && (
                  <Button size="sm" variant="outline" className="gap-1 text-destructive" onClick={() => { deleteCompany(c.id); toast({ title: "Deleted", description: c.name }); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* View Company Dialog */}
      <Dialog open={!!viewCompany} onOpenChange={() => setViewCompany(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {viewCompany && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" /> {viewCompany.name}
                  <Badge variant={viewCompany.status === "Active" ? "default" : "secondary"}>{viewCompany.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-3">
                <div className="space-y-3">
                  <div><p className="text-xs text-muted-foreground">Legal Name</p><p className="text-sm text-foreground">{viewCompany.legalName}</p></div>
                  <div><p className="text-xs text-muted-foreground">GSTIN</p><code className="text-sm bg-muted px-2 py-0.5 rounded">{viewCompany.gstin}</code></div>
                  <div><p className="text-xs text-muted-foreground">PAN</p><p className="text-sm text-foreground">{viewCompany.pan}</p></div>
                  {viewCompany.cin && <div><p className="text-xs text-muted-foreground">CIN</p><p className="text-sm text-foreground">{viewCompany.cin}</p></div>}
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5" /><p className="text-sm text-foreground">{viewCompany.address}, {viewCompany.city}, {viewCompany.state} - {viewCompany.pincode}</p></div>
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><p className="text-sm text-foreground">{viewCompany.phone}</p></div>
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><p className="text-sm text-foreground">{viewCompany.email}</p></div>
                  {viewCompany.website && <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-muted-foreground" /><p className="text-sm text-foreground">{viewCompany.website}</p></div>}
                </div>
              </div>
              <div className="flex gap-3 py-2">
                <Badge variant="outline">GST: {viewCompany.gstRegistered ? "Registered" : "Not Registered"}</Badge>
                <Badge variant="outline">FY: April - March</Badge>
                <Badge variant="outline">Plan: {viewCompany.plan}</Badge>
                {viewCompany.compositionScheme && <Badge variant="outline">Composition Scheme</Badge>}
              </div>

              {/* Members */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Users className="h-4 w-4" /> Authorized Members</h3>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => setShowMemberForm(viewCompany.id)}>
                    <UserPlus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewCompany.members.map((m) => (
                      <TableRow key={m.userId}>
                        <TableCell className="font-medium text-foreground">{m.userName}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{m.email}</TableCell>
                        <TableCell><Badge variant="secondary" className="capitalize">{m.role}</Badge></TableCell>
                        <TableCell className="text-sm">{m.joinedAt}</TableCell>
                        <TableCell className="text-right">
                          {m.role !== "owner" && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => {
                              removeMember(viewCompany.id, m.userId);
                              setViewCompany({ ...viewCompany, members: viewCompany.members.filter((mem) => mem.userId !== m.userId) });
                              toast({ title: "Removed", description: m.userName });
                            }}>
                              <UserMinus className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={!!editCompany} onOpenChange={() => setEditCompany(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {editCompany && (
            <>
              <DialogHeader><DialogTitle>Edit {editCompany.name}</DialogTitle></DialogHeader>
              <div className="grid gap-3 py-2">
                <div className="p-3 bg-muted/40 border border-border rounded-lg">
                  <GSTINVerificationInput
                    value={editCompany.gstin}
                    onChange={(val) => {
                      const extracted = extractFromGSTIN(val);
                      setEditCompany({
                        ...editCompany,
                        gstin: val,
                        pan: extracted.pan || editCompany.pan,
                        state: extracted.state?.name || editCompany.state,
                      });
                    }}
                    onApplyVerification={handleApplyGstVerificationForEdit}
                    currentFormValues={{
                      name: editCompany.name,
                      legalName: editCompany.legalName,
                      pan: editCompany.pan,
                      address: editCompany.address,
                      city: editCompany.city,
                      state: editCompany.state,
                      pincode: editCompany.pincode,
                      compositionScheme: editCompany.compositionScheme,
                    }}
                    isEditMode={true}
                    label="GSTIN Verification & Sync"
                  />
                </div>
                <div><Label>Company Name</Label><Input value={editCompany.name} onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })} /></div>
                <div><Label>Legal Name</Label><Input value={editCompany.legalName} onChange={(e) => setEditCompany({ ...editCompany, legalName: e.target.value })} /></div>
                <div><Label>PAN</Label><Input value={editCompany.pan} onChange={(e) => setEditCompany({ ...editCompany, pan: e.target.value })} maxLength={10} /></div>
                <div><Label>Address</Label><Input value={editCompany.address} onChange={(e) => setEditCompany({ ...editCompany, address: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={editCompany.city} onChange={(e) => setEditCompany({ ...editCompany, city: e.target.value })} /></div>
                  <div>
                    <Label>State</Label>
                    <Select value={editCompany.state} onValueChange={(v) => setEditCompany({ ...editCompany, state: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Pincode</Label><Input value={editCompany.pincode} onChange={(e) => setEditCompany({ ...editCompany, pincode: e.target.value })} maxLength={6} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input value={editCompany.phone} onChange={(e) => setEditCompany({ ...editCompany, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input value={editCompany.email} onChange={(e) => setEditCompany({ ...editCompany, email: e.target.value })} /></div>
                </div>
                <div className="flex items-center justify-between py-1">
                  <Label>Status</Label>
                  <Select value={editCompany.status} onValueChange={(v) => setEditCompany({ ...editCompany, status: v as Company["status"] })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Trial">Trial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleEdit} className="w-full">Save Changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={!!showMemberForm} onOpenChange={() => setShowMemberForm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Name</Label><Input value={mf.userName} onChange={(e) => setMF({ ...mf, userName: e.target.value })} placeholder="Full name" /></div>
            <div><Label>Email</Label><Input value={mf.email} onChange={(e) => setMF({ ...mf, email: e.target.value })} placeholder="email@company.com" /></div>
            <div>
              <Label>Role</Label>
              <Select value={mf.role} onValueChange={(v) => setMF({ ...mf, role: v as CompanyMember["role"] })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="ca">Chartered Accountant</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAddMember} className="w-full gap-2"><UserPlus className="h-4 w-4" /> Add Member</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
